<template>
    <div
        class="package-details"
        :class="`package-details--${ticket.location_slug}`"
    >
        <div class="container">
            <div
                @click="goBack"
                class="back-button"
                type="button"
                role="button"
            >
                <svg-icon name="arrow-left" />
                {{ trans.details.back_to_packages }}
            </div>

            <div class="content">
                <ticket-package-gallery
                    :featured-image="ticket.img"
                    :images="ticket.imgs"
                    :trans="trans"
                />

                <div class="info">
                    <div class="package-details-date">
                        <strong class="date">{{ formattedDate }}</strong>
                        -
                        <span class="day">{{ dayOfWeek }}</span>
                        |
                        <span class="group">{{ ticket.group }}</span>
                    </div>

                    <div class="package-details-title">
                        <h1>{{ translatedLocationName }}</h1>
                        <span
                            class="badge"
                            :class="`badge-${ticket.location_slug}`"
                        >
                            {{ `${trans.tickets.sector} ${ticket.sector}` }}
                        </span>
                        <ticket-packages-type
                            :type="
                                ticket.is_digital
                                    ? 'digital-ticket'
                                    : 'physical-ticket'
                            "
                            :trans="trans.seat"
                        />
                    </div>

                    <section class="package-details-description">
                        <small>{{ trans.details.description_location }}</small>

                        <p
                            :class="{ excerpt: excerpt }"
                            v-html="ticket.details_description"
                        />

                        <a
                            href="#"
                            class="button-see-more"
                            @click.prevent="toggleExcerpt"
                            >{{
                                excerpt
                                    ? trans.details.see_more
                                    : trans.details.see_less
                            }}</a
                        >
                    </section>

                    <section class="package-details-map">
                        <small>{{ trans.details.your_location }}</small>
                        <map-sambadrome
                            :location="ticket.location_slug"
                            :sector="ticket.sector"
                        />
                    </section>

                    <section class="package-details-includes">
                        <small>{{ trans.details.package_includes }}</small>
                        <div class="includes-list">
                            <span
                                class="includes-item"
                                v-for="additional in ticket.additionals"
                                :key="additional.id"
                            >
                                <img
                                    :src="additional.icon"
                                    :alt="additional.name"
                                    :title="additional.name"
                                    class="include-icon"
                                />
                                {{ additional.name }}
                            </span>
                        </div>
                    </section>

                    <section
                        class="package-details-included-free"
                        v-if="ticket.includes && ticket.includes.length"
                        v-for="include in ticket.includes"
                        :key="include.title"
                    >
                        <figure class="included-free-image">
                            <span class="included-free-label">
                                <i class="fa fa-gift"></i>
                                {{ trans.details.included }}
                            </span>
                            <img :src="include.image" :alt="include.title" />
                        </figure>
                        <div class="included-free-content">
                            <div class="included-free-text">
                                <h3>{{ include.title }}</h3>
                                <small v-html="include.short_description" />
                            </div>

                            <div class="included-free-value">
                                <span>
                                    {{ trans.details.value }}:
                                    <strong>US${{ include.price }}</strong>
                                    /{{ trans.details.person }}
                                </span>
                                <strong class="free">{{
                                    trans.details.free
                                }}</strong>
                            </div>
                        </div>
                    </section>

                    <section class="package-details-summary">
                        <div
                            class="promocode-group"
                            :class="{ applied: promocodeApplied }"
                            v-if="this.allowPromocode"
                        >
                            <input
                                type="text"
                                v-model="promocode"
                                @input="() => (promocodeApplied = false)"
                                :placeholder="trans.promocode.input"
                                autocomplete="off"
                            />
                            <button
                                class="button button-primary"
                                @click.prevent="applyPromocode"
                                :disabled="loading"
                            >
                                {{
                                    loading
                                        ? "..."
                                        : promocodeApplied
                                        ? trans.promocode.applied
                                        : trans.promocode.apply
                                }}
                            </button>
                        </div>

                        <strong>{{ trans.details.order_summary }}</strong>
                        <ul class="summary-list">
                            <li class="summary-item">
                                <span>{{
                                    trans.details.ticket_package_price
                                }}</span>
                                <span>{{ formatPrice(price) }}</span>
                            </li>
                            <li
                                class="summary-item price-discount"
                                v-if="
                                    this.allowPromocode &&
                                        promocodeApplied &&
                                        promocodeData
                                "
                            >
                                <span
                                    >{{ trans.details.promo_code_applied }} ({{
                                        promocodeData.promocode
                                    }})</span
                                >
                                <span>- {{ discountDisplay }}</span>
                            </li>
                            <li class="summary-item">
                                <span>{{ trans.details.fees }}</span>
                                <span>{{ formatPrice(fee) }}</span>
                            </li>
                            <li class="summary-item">
                                <strong>{{ trans.details.total }}</strong>
                                <strong>{{ formatPrice(totalPrice) }}</strong>
                            </li>
                        </ul>

                        <div class="summary-actions">
                            <button
                                class="button button-primary button-outline"
                                :disabled="ticket.status !== 'enable'"
                                @click="addToCart"
                            >
                                {{ trans.details.add_to_cart }}
                            </button>
                            <button
                                class="button button-primary"
                                :disabled="ticket.status !== 'enable'"
                                @click="buyNow"
                            >
                                {{
                                    ticket.status === "enable"
                                        ? trans.details.buy_now
                                        : ticket.status_text ||
                                          trans.tickets.sold_out ||
                                          "Sold out"
                                }}
                            </button>
                        </div>
                    </section>
                </div>

                <section class="map">
                    <small>{{ trans.details.your_location }}</small>
                    <map-sambadrome
                        :location="ticket.location_slug"
                        :sector="ticket.sector"
                        orientation="vertical"
                    />
                </section>
            </div>
        </div>
    </div>
</template>

<script>
import MapSambadrome from "../../map/MapSambadrome.vue";
import TicketPackageGallery from "./TicketPackageGallery.vue";

export default {
    components: {
        TicketPackageGallery,
        MapSambadrome
    },
    props: {
        page: {
            type: Object,
            required: true
        },
        priceType: {
            type: String,
            required: true
        },
        ticket: {
            type: Object,
            required: true
        },
        trans: {
            type: Object,
            required: true
        },
        lang: {
            type: String,
            required: false,
            default: 'en'
        },
        locations: {
            type: Array,
            required: false,
            default: () => []
        }
    },
    data() {
        return {
            locationsWithoutPromoCode: [
                "luxury-suite",
                "folia-tropical",
                "super-folia",
                "camarote-maravilha"
            ],
            excerpt: true,
            promocodeApplied: false,
            promocode: "",
            promocodeData: null,
            loading: false,
            userToken: null,
            userData: null
        };
    },
    mounted() {
        this.loadPromocodeFromStorage();
        this.checkUserAuthentication();

        window.eventBus.$on("resume-updated", this.updateLayoutBeforeResume);
    },
    beforeDestroy() {
        window.eventBus.$off("resume-updated", this.updateLayoutBeforeResume);
    },
    computed: {
        dateLocale() {
            const locales = {
                "pt": 'pt-BR',
                "en": 'en-US',
                "es": 'es-ES',
                "it": 'it-IT',
                "fr": 'fr-FR',
                "tr": 'tr-TR',
                "de": 'de-DE',
            };

            if (this.lang && locales[this.lang]) {
                return locales[this.lang];
            }

            return this.ticket.currency === "BRL" ? "pt-BR" : "en-US";
        },
        currencyLocale() {
            return this.ticket.currency === "BRL" ? "pt-BR" : "en-US";
        },
        formattedDate() {
            if (!this.ticket.small_date) return "";

            try {
                const [year, month, day] = this.ticket.small_date.split("-");
                const date = new Date(
                    parseInt(year),
                    parseInt(month) - 1,
                    parseInt(day)
                );

                return date.toLocaleDateString(this.dateLocale, {
                    day: "numeric",
                    month: "short",
                    year: "numeric"
                });
            } catch (error) {
                console.warn("Erro ao formatar data:", error);
                return this.ticket.small_date;
            }
        },
        dayOfWeek() {
            if (!this.ticket.small_date) return "";

            try {
                const [year, month, day] = this.ticket.small_date.split("-");
                const date = new Date(
                    parseInt(year),
                    parseInt(month) - 1,
                    parseInt(day)
                );

                return date.toLocaleDateString(this.dateLocale, {
                    weekday: "long"
                });
            } catch (error) {
                console.warn("Erro ao formatar dia da semana:", error);
                return "";
            }
        },
        currencyPrefix() {
            return this.ticket.currency === "BRL" ? "R$" : "US$";
        },
        price() {
            return this.ticket.price || 0;
        },
        fee() {
            return this.ticket.fee || 0;
        },
        discountCoefficient() {
            if (!this.promocodeApplied || !this.promocodeData) {
                return 1;
            }
            return this.promocodeData.discount;
        },
        discountAmount() {
            if (!this.promocodeApplied || !this.promocodeData) {
                return 0;
            }

            if (this.promocodeData.promo_type === 'fixed') {
                // Fixed discount: use the discount value directly
                return this.promocodeData.discount;
            } else {
                // Percent discount: calculate based on price and coefficient
                return this.price - this.price * this.discountCoefficient;
            }
        },
        discountPercentage() {
            if (!this.promocodeApplied || !this.promocodeData) {
                return 0;
            }
            return Math.round((1 - this.discountCoefficient) * 100);
        },
        discountDisplay() {
            if (!this.promocodeApplied || !this.promocodeData) {
                return '';
            }

            if (this.promocodeData.promo_type === 'fixed') {
                return this.formatPrice(this.promocodeData.discount);
            } else {
                return `${this.discountPercentage}%`;
            }
        },
        totalPrice() {
            const basePrice = this.ticket.price + this.fee;
            const finalPrice = basePrice - this.discountAmount;

            if (this.promocodeApplied && this.promocodeData) {
                return Math.floor(finalPrice);
            }

            return finalPrice;
        },
        allowPromocode() {
            return (
                this.ticket.allow_promocode &&
                !this.locationsWithoutPromoCode.includes(
                    this.ticket.location_slug
                )
            );
        },
        translatedLocationName() {
            if (!this.locations || this.locations.length === 0) {
                return this.ticket.location || this.ticket.location_slug;
            }

            const translatedLocation = this.locations.find(
                loc => loc.slug_en === this.ticket.location_slug
            );

            if (translatedLocation && translatedLocation.name) {
                return translatedLocation.name;
            }

            const translatedLocationBySlug = this.locations.find(
                loc => loc.slug === this.ticket.location_slug
            );

            if (translatedLocationBySlug && translatedLocationBySlug.name) {
                return translatedLocationBySlug.name;
            }

            return this.ticket.location || this.ticket.location_slug;
        }
    },
    methods: {
        toggleExcerpt() {
            this.excerpt = !this.excerpt;
        },
        formatPrice(price) {
            return price.toLocaleString(this.currencyLocale, {
                style: "currency",
                currency: this.ticket.currency === "BRL" ? "BRL" : "USD"
            });
        },
        promoCoversTicket(data) {
            if (!data) return false;
            const ids = [this.ticket.id, this.ticket.sku_id]
                .filter(value => value !== undefined && value !== null && value !== "")
                .map(String);
            const eligible = []
                .concat(data.tickets || [], data.legacy_ticket_ids || [])
                .map(String);
            return ids.some(id => eligible.indexOf(id) !== -1);
        },
        applyPromocode() {
            this.loading = true;
            const request = axios.get("/api/promocode", {
                params: {
                    promocode: this.promocode,
                    site: this.trans.domain.site
                }
            });
            request
                .then(({ data }) => {
                    this.loading = false;

                    if (!data.tickets || data.tickets.length === 0 || !this.promoCoversTicket(data)) {
                        window.toast.error(this.trans.promocode.invalid);
                        this.clearPromocode();
                        return;
                    }

                    this.promocodeApplied = true;
                    this.promocodeData = {
                        promocode: this.promocode,
                        ...data
                    };

                    this.savePromocodeToStorage(this.promocodeData);
                    window.eventBus.$emit("fetch-endpoint-with-token");

                    window.toast.success(this.trans.promocode.success);
                })
                .catch(_error => {
                    this.loading = false;
                    window.toast.error(this.trans.promocode.error);
                });
        },
        savePromocodeToStorage(promocodeData) {
            try {
                sessionStorage.setItem(
                    "promocodeData",
                    JSON.stringify(promocodeData)
                );
            } catch (error) {
                console.warn(
                    "Failed to save promocode to sessionStorage:",
                    error
                );
            }
        },
        loadPromocodeFromStorage() {
            try {
                if (!this.allowPromocode) {
                    return;
                }

                const storedData = sessionStorage.getItem("promocodeData");
                if (storedData) {
                    const promocodeData = JSON.parse(storedData);

                    if (!this.promoCoversTicket(promocodeData)) {
                        return;
                    }

                    this.promocodeData = promocodeData;
                    this.promocode = promocodeData.promocode;
                    this.promocodeApplied = true;
                }
            } catch (error) {
                console.warn(
                    "Failed to load promocode from sessionStorage:",
                    error
                );
            }
        },
        clearPromocode() {
            this.promocode = "";
            this.promocodeApplied = false;
            this.promocodeData = null;
            sessionStorage.removeItem("promocodeData");
        },
        checkUserAuthentication() {
            this.userToken = this.getToken();
        },
        getToken() {
            const queryParams = new URLSearchParams(window.location.search);

            if (queryParams.has("token")) {
                const token = queryParams.get("token");
                localStorage.setItem("token", queryParams.get("token"));
                this.cleanUrlFromToken();
                return token;
            } else {
                const tokenFromLocalStorage = localStorage.getItem("token");
                if (tokenFromLocalStorage) {
                    return tokenFromLocalStorage;
                } else {
                    return null;
                }
            }
        },
        cleanUrlFromToken() {
            const url = new URL(window.location);
            url.searchParams.delete("token");
            window.history.replaceState(
                {},
                document.title,
                url.pathname + url.search
            );
        },
        isUserAuthenticated() {
            return !!this.userToken;
        },
        redirectToSSO(includeReturnUrl = true, onlyGoToCart = false) {
            const bookersShopUrl =
                window.bookersShopUrl || "https://bookers-shop.com/";

            const ssoParams = new URLSearchParams({
                site: this.trans.domain.site,
                ticket_id: this.ticket.id,
                price_type: this.ticket.price_type || this.priceType,
                promocode: this.promocodeApplied ? this.promocode : ""
            });

            if (onlyGoToCart) {
                const cartUrl = `${bookersShopUrl}my-cart.asp?site=${
                    this.trans.domain.site
                }`;
                window.location.href = cartUrl;

                return;
            }

            if (includeReturnUrl) {
                ssoParams.append("return_url", this.getTicketListPage());
            }

            const ssoUrl = `${bookersShopUrl}sso/register.asp?${ssoParams.toString()}`;
            window.location.href = ssoUrl;
        },
        redirectToShopV2Buy(returnUrl) {
            const domain = this.trans.domain || {};
            const siteUuid = domain.site_uuid
                ? String(domain.site_uuid).trim()
                : "";
            const url = this.buildTicketBuyUrl({
                skuId: this.ticket.sku_id || this.ticket.id,
                quantity: 1,
                site: siteUuid,
                priceType: this.ticket.price_type || this.priceType,
                currency: domain.shop_currency,
                promocode: this.promocodeApplied ? this.promocode : null,
                returnUrl: returnUrl || null
            });

            if (
                window.BookersGtm &&
                typeof window.BookersGtm.goToShopUrl === "function"
            ) {
                window.BookersGtm.goToShopUrl(url);
                return;
            }

            window.location.href = url;
        },
        buildTicketBuyUrl(opts) {
            const raw =
                window.bookersShopUrl || "https://bookers-shop.com/";
            const base = raw.endsWith("/") ? raw : raw + "/";
            const url = new URL("buy/ticket", base);
            const skuId = opts.skuId && String(opts.skuId).trim();

            if (!skuId) {
                throw new Error("buildTicketBuyUrl: skuId required");
            }

            const quantity = Math.min(99, Math.max(1, Number(opts.quantity) || 1));
            url.searchParams.set("sku_id", skuId);
            url.searchParams.set("quantity", String(quantity));

            if (opts.site) {
                url.searchParams.set("site", String(opts.site));
            }
            if (opts.priceType) {
                url.searchParams.set("price_type", opts.priceType);
            }
            if (opts.currency) {
                url.searchParams.set("currency", String(opts.currency));
            }
            if (opts.promocode) {
                url.searchParams.set("promocode", opts.promocode);
            }
            if (opts.returnUrl) {
                url.searchParams.set("return_url", opts.returnUrl);
            }

            return url.toString();
        },
        async addToCart() {
            if (this.ticket.status !== "enable") {
                return;
            }
            this.redirectToShopV2Buy(window.location.href);
        },
        async buyNow() {
            if (this.ticket.status !== "enable") {
                return;
            }
            this.redirectToShopV2Buy();
        },
        getTicketListPage() {
            const url = new URL(window.location.href);
            url.searchParams.delete("hash");
            return url.toString();
        },
        goBack() {
            const route = String(this.page.route || "").replace(
                /^\/+|\/+$/g,
                ""
            );
            const pageUrl = new URL(`${window.location.origin}/${route}`);
            const currentUrlParams = new URLSearchParams(
                window.location.search
            );

            currentUrlParams.forEach((value, key) => {
                pageUrl.searchParams.set(key, value);
            });

            window.location.href = pageUrl.toString();
        },
        updateLayoutBeforeResume(newUserData) {
            this.userData = newUserData;
        }
    }
};
</script>

<style lang="scss" scoped>
.package-details-includes .include-icon {
    width: 20px;
    height: 20px;
    max-width: 20px;
    margin-right: 8px;
    vertical-align: middle;
    flex-shrink: 0;
}
</style>
